import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Match, MatchType } from '../../types/sports';
import { LocalMatch } from '../../types/localMatchday';
import { matchdayPlanningApi } from '../../services/matchdayPlanningApi';
import { matchdayPlanningEntitiesApi } from '../../services/matchdayPlanningEntitiesApi';
import { VehicleAssignment, VehicleType, StaffAssignment, StaffRole, TravelerInfo, MatchdayPlanning } from '../../types/matchdayPlanning';
import RouteMap from '../matchdayPlanning/RouteMap';
import { PlanningRouteResult } from '../../services/tomtomApi';
import { exportToCalendar, matchToCalendarEvent, localMatchToCalendarEvent } from '../../utils/calendarExport';

// Vehicle type icons for compact display
const VEHICLE_TYPE_ICONS: Record<VehicleType, string> = {
  'bus': 'Bus',
  'minibus': 'Minibus',
  'car': 'PKW',
  'plane': 'Flug',
  'train': 'Bahn',
  'taxi': 'Taxi',
  'rentalcar': 'Mietwagen',
  'ship': 'Schiff',
  'bicycle': 'Rad',
  'onfoot': 'Fuß'
};

interface MatchPlanningInfo {
  matchId: number;
  planningId?: number;
  vehicleAssignments: VehicleAssignment[];
  staffAssignments: StaffAssignment[];
}

interface PlantafelProps {
  matches: (Match | LocalMatch)[];
  onPlanMatch?: (match: Match) => void;
  onDeleteMatch?: (match: Match) => void;
  userClubId?: number;
  homeMatchColor?: string;
  awayMatchColor?: string;
  sortOrder?: 'asc' | 'desc';
  showPastMatches?: boolean;
}

interface WeekData {
  weekNumber: number;
  weekStart: Date;
  weekEnd: Date;
  matches: (Match | LocalMatch)[];
  isEnglishWeek: boolean;
}

// Convert hex color to rgba with alpha (0-1)
const hexToRgba = (hex: string, alpha: number): string => {
  const h = hex.replace('#', '');
  const r = parseInt(h.substring(0, 2), 16);
  const g = parseInt(h.substring(2, 4), 16);
  const b = parseInt(h.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const Plantafel: React.FC<PlantafelProps> = ({
  matches,
  onPlanMatch,
  onDeleteMatch,
  userClubId,
  homeMatchColor = '#d4edda',
  awayMatchColor = '#f8d7da',
  sortOrder = 'asc',
  showPastMatches: showPastMatchesProp,
}) => {
  const navigate = useNavigate();
  // State for planning info per match
  const [planningInfo, setPlanningInfo] = useState<Map<number, MatchPlanningInfo>>(new Map());
  // State for available staff members
  const [staffMembers, setStaffMembers] = useState<TravelerInfo[]>([]);
  // State for saving indicator
  const [savingCell, setSavingCell] = useState<string | null>(null);
  // Use prop to control past matches visibility (inverted: showPast = !hide)
  const hidePastMatches = showPastMatchesProp !== undefined ? !showPastMatchesProp : true;
  // State for route modal
  const [routeModalOpen, setRouteModalOpen] = useState<boolean>(false);
  const [selectedPlanningId, setSelectedPlanningId] = useState<number | null>(null);
  const [routeResult, setRouteResult] = useState<PlanningRouteResult | null>(null);

  // Load staff members on mount
  useEffect(() => {
    const loadStaffMembers = async () => {
      try {
        const members = await matchdayPlanningEntitiesApi.getStaffMembers();
        setStaffMembers(members);
      } catch (error) {
        console.error('Error loading staff members:', error);
      }
    };
    loadStaffMembers();
  }, []);

  // Load planning info for all matches in a single batch request
  useEffect(() => {
    const loadPlanningInfo = async () => {
      const newPlanningInfo = new Map<number, MatchPlanningInfo>();

      // Get all matches that have a matchId (API matches, not local)
      const apiMatches = matches.filter(m => 'homeClubId' in m);
      if (apiMatches.length === 0) {
        setPlanningInfo(newPlanningInfo);
        return;
      }

      try {
        const matchIds = apiMatches.map(m => m.id);
        const plannings = await matchdayPlanningApi.getPlanningsByEvents(matchIds);

        plannings.forEach(planning => {
          if (planning.matchId) {
            newPlanningInfo.set(planning.matchId, {
              matchId: planning.matchId,
              planningId: planning.id,
              vehicleAssignments: planning.vehicleAssignments || [],
              staffAssignments: planning.staffAssignments || []
            });
          }
        });
      } catch {
        // Silently ignore - batch endpoint may fail if no plannings exist
      }

      setPlanningInfo(newPlanningInfo);
    };

    if (matches.length > 0) {
      loadPlanningInfo();
    }
  }, [matches]);

  // Get vehicle assignments for a match
  const getMatchVehicleAssignments = (matchId: number): VehicleAssignment[] => {
    return planningInfo.get(matchId)?.vehicleAssignments || [];
  };

  // Get staff assignments for a match
  const getMatchStaffAssignments = (matchId: number): StaffAssignment[] => {
    return planningInfo.get(matchId)?.staffAssignments || [];
  };

  // Role can come as number or string from backend - handle both
  const roleMap: Record<string, StaffRole> = {
    'Physio': StaffRole.Physio,
    'Arzt': StaffRole.Arzt,
    'Athletik': StaffRole.Athletik,
    '0': StaffRole.Physio,
    '1': StaffRole.Arzt,
    '2': StaffRole.Athletik
  };

  // Get staff by role for a match
  const getStaffByRole = (matchId: number, role: StaffRole): StaffAssignment | undefined => {
    const assignments = getMatchStaffAssignments(matchId);
    return assignments.find(sa => {
      const saRole = typeof sa.role === 'string' ? roleMap[sa.role] : sa.role;
      return saRole === role;
    });
  };

  // Get planning ID for a match
  const getPlanningId = (matchId: number): number | undefined => {
    return planningInfo.get(matchId)?.planningId;
  };

  // Handle staff assignment change
  const handleStaffChange = async (match: Match, role: StaffRole, travelerId: number | null) => {
    const cellKey = `${match.id}-${role}`;
    setSavingCell(cellKey);

    try {
      let planningId = getPlanningId(match.id);

      // If no planning exists, create one first
      if (!planningId) {
        // Use 'date' field for correct time (not scheduledDate which may have timezone offset)
        const matchDateStr = match.date;
        const matchDate = new Date(matchDateStr);
        const homeName = match.homeClub?.name || 'Heim';
        const awayName = match.awayClub?.name || 'Gast';
        const newPlanning = await matchdayPlanningApi.createPlanning({
          matchId: match.id,
          name: `${homeName} vs ${awayName}`,
          date: matchDateStr,
          kickoffTime: matchDate.toTimeString().slice(0, 5),
          arrivalLeadMinutes: 60,
        });
        planningId = newPlanning.id;

        // Update local state with new planning
        const current = planningInfo.get(match.id) || {
          matchId: match.id,
          vehicleAssignments: [],
          staffAssignments: []
        };
        current.planningId = planningId;
        setPlanningInfo(new Map(planningInfo.set(match.id, current)));
      }

      const existingAssignment = getStaffByRole(match.id, role);

      if (travelerId === null || travelerId === 0) {
        // Delete assignment if exists
        if (existingAssignment) {
          await matchdayPlanningApi.deleteStaffAssignment(existingAssignment.id);
          // Update local state
          const current = planningInfo.get(match.id)!;
          current.staffAssignments = current.staffAssignments.filter(sa => sa.id !== existingAssignment.id);
          setPlanningInfo(new Map(planningInfo.set(match.id, current)));
        }
      } else if (existingAssignment) {
        // Update existing assignment
        const updated = await matchdayPlanningApi.updateStaffAssignment(existingAssignment.id, {
          travelerId: travelerId
        });
        // Update local state
        const current = planningInfo.get(match.id)!;
        const idx = current.staffAssignments.findIndex(sa => sa.id === existingAssignment.id);
        if (idx >= 0) {
          current.staffAssignments[idx] = updated;
        }
        setPlanningInfo(new Map(planningInfo.set(match.id, current)));
      } else {
        // Create new assignment
        const created = await matchdayPlanningApi.createStaffAssignment({
          planningId: planningId!,
          travelerId: travelerId,
          role: role
        });
        // Update local state
        const current = planningInfo.get(match.id) || {
          matchId: match.id,
          planningId: planningId,
          vehicleAssignments: [],
          staffAssignments: []
        };
        current.staffAssignments.push(created);
        setPlanningInfo(new Map(planningInfo.set(match.id, current)));
      }
    } catch (error) {
      console.error('Error updating staff assignment:', error);
      alert('Fehler beim Speichern der Staff-Zuordnung');
    } finally {
      setSavingCell(null);
    }
  };

  // Calculate ISO week number
  const getWeekNumber = (date: Date): number => {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
  };

  // Get week start (Monday) and end (Sunday)
  const getWeekBounds = (date: Date): { start: Date; end: Date } => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    const start = new Date(d.setDate(diff));
    start.setHours(0, 0, 0, 0);
    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    end.setHours(23, 59, 59, 999);
    return { start, end };
  };

  // Group matches by week
  const groupByWeek = (): WeekData[] => {
    const weekMap = new Map<string, WeekData>();

    matches.forEach(match => {
      const matchDate = new Date(getMatchDateString(match));
      const { start, end } = getWeekBounds(matchDate);
      const weekKey = start.toISOString();

      if (!weekMap.has(weekKey)) {
        weekMap.set(weekKey, {
          weekNumber: getWeekNumber(matchDate),
          weekStart: start,
          weekEnd: end,
          matches: [],
          isEnglishWeek: false,
        });
      }
      weekMap.get(weekKey)!.matches.push(match);
    });

    // Mark English weeks (2+ matches for user's club)
    weekMap.forEach(week => {
      if (userClubId) {
        const userMatches = week.matches.filter(m => {
          if ('homeClubId' in m) {
            return m.homeClubId === userClubId || m.awayClubId === userClubId;
          }
          return true;
        });
        week.isEnglishWeek = userMatches.length >= 2;
      } else {
        week.isEnglishWeek = week.matches.length >= 2;
      }
    });

    // Sort weeks based on sortOrder prop
    const weeks = Array.from(weekMap.values());
    weeks.sort((a, b) => {
      const diff = a.weekStart.getTime() - b.weekStart.getTime();
      return sortOrder === 'asc' ? diff : -diff;
    });

    // Also sort matches within each week
    weeks.forEach(week => {
      week.matches.sort((a, b) => {
        const dateA = new Date(getMatchDateString(a)).getTime();
        const dateB = new Date(getMatchDateString(b)).getTime();
        return sortOrder === 'asc' ? dateA - dateB : dateB - dateA;
      });
    });

    return weeks;
  };

  // Determine if home match
  const isHomeMatch = (match: Match | LocalMatch): boolean | null => {
    if (!userClubId) return null;
    if ('homeClubId' in match) {
      if (match.homeClubId === userClubId) return true;
      if (match.awayClubId === userClubId) return false;
    }
    return null;
  };

  // Get opponent name (supports own matches with free-text team names)
  const getOpponent = (match: Match | LocalMatch): string => {
    if ('homeTeam' in match && typeof match.homeTeam === 'string') {
      return `${match.homeTeam} vs ${match.awayTeam}`;
    }
    const m = match as Match;
    const homeLabel = m.homeClub?.name || m.homeTeamName || null;
    const awayLabel = m.awayClub?.name || m.awayTeamName || null;
    const isHome = isHomeMatch(match);
    if (isHome === true) return awayLabel || 'Unbekannt';
    if (isHome === false) return homeLabel || 'Unbekannt';
    if (homeLabel && awayLabel) return `${homeLabel} vs ${awayLabel}`;
    return homeLabel || awayLabel || 'Unbekannt';
  };

  // Get the correct date field for a match
  // For API matches (Match), prefer 'date' field (correct time) over 'scheduledDate' (may have timezone offset)
  // For LocalMatch, use 'scheduledDate'
  const getMatchDateString = (match: Match | LocalMatch): string => {
    if ('homeClubId' in match) {
      // This is an API Match - use 'date' field which has the correct time
      return (match as Match).date;
    }
    // This is a LocalMatch
    return (match as LocalMatch).scheduledDate;
  };

  // Format date
  const formatDate = (dateStr: string): string => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit' });
  };

  // Format time
  const formatTime = (dateStr: string): string => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  };

  const weeks = groupByWeek();

  // Check if match is in the past
  const isPastMatch = (matchDateStr: string): boolean => {
    const matchDate = new Date(matchDateStr);
    const now = new Date();
    return matchDate < now;
  };

  // Filter weeks based on hidePastMatches toggle
  const filteredWeeks = hidePastMatches
    ? weeks.map(week => ({
        ...week,
        matches: week.matches.filter(m => {
          return !isPastMatch(getMatchDateString(m));
        })
      })).filter(week => week.matches.length > 0)
    : weeks;

  // Count past matches for display
  const pastMatchCount = matches.filter(m => {
    return isPastMatch(getMatchDateString(m));
  }).length;

  // Open route modal for a match
  const openRouteModal = (matchId: number) => {
    const info = planningInfo.get(matchId);
    if (info?.planningId) {
      setSelectedPlanningId(info.planningId);
      setRouteResult(null);
      setRouteModalOpen(true);
    }
  };

  // Close route modal
  const closeRouteModal = () => {
    setRouteModalOpen(false);
    setSelectedPlanningId(null);
    setRouteResult(null);
  };

  // Check if match has a planning
  const hasPlanning = (matchId: number): boolean => {
    const info = planningInfo.get(matchId);
    return !!info?.planningId;
  };

  // SVG icon helpers (inline, no emoji)
  const iconCalendar = (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
    </svg>
  );
  const iconRoute = (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="10" r="3"/><path d="M12 21.7C17.3 17 20 13 20 10a8 8 0 1 0-16 0c0 3 2.7 7 8 11.7z"/>
    </svg>
  );
  const iconTracking = (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
    </svg>
  );
  const iconTrash = (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/><line x1="10" y1="11" x2="10" y2="17"/><line x1="14" y1="11" x2="14" y2="17"/>
    </svg>
  );

  return (
    <div style={containerStyle}>

      {/* Grid Header – uses .timeline-grid-header CSS, only override grid-template-columns */}
      <div className="timeline-grid-header" style={gridColsOverride}>
        <span className="header-cell">KW</span>
        <span className="header-cell">DATUM</span>
        <span className="header-cell">ZEIT</span>
        <span className="header-cell">GEGNER</span>
        <span className="header-cell" style={{ textAlign: 'center' }}>H/A</span>
        <span className="header-cell">WETTBEWERB</span>
        <span className="header-cell">FAHRZEUGE</span>
        <span className="header-cell">PHYSIO</span>
        <span className="header-cell">ARZT</span>
        <span className="header-cell">ATHLETIK</span>
        <span className="header-cell header-actions">AKT.</span>
      </div>

      {/* Body – like .timeline-body */}
      <div className="timeline-body">
      {filteredWeeks.map(week => (
        <React.Fragment key={week.weekStart.toISOString()}>
          {week.matches.map((match) => {
            const isHome = isHomeMatch(match);
            const matchDate = getMatchDateString(match);
            const isPast = isPastMatch(matchDate);
            const borderColor = isHome === true ? (homeMatchColor || '#4caf50') : isHome === false ? (awayMatchColor || '#f44336') : '#dee2e6';
            // Subtle tinted row background like Spieltagsplanung "Anstoß" row (#fff8f8)
            const rowBg = isHome === true ? hexToRgba(homeMatchColor, 0.18) : isHome === false ? hexToRgba(awayMatchColor, 0.18) : 'white';

            return (
              <div
                key={match.id}
                className="segment-item"
                style={{
                  ...segmentRowStyle,
                  borderLeftColor: borderColor,
                  borderLeftWidth: week.isEnglishWeek ? '4px' : '3px',
                  opacity: isPast ? 0.5 : 1,
                  background: rowBg,
                }}
              >
                <span style={kwColStyle}>
                  {week.weekNumber}
                  {week.isEnglishWeek && <span style={englishWeekBadgeStyle}>EW</span>}
                </span>
                <span>{formatDate(matchDate)}</span>
                <span>{formatTime(matchDate)}</span>
                <span style={{ fontWeight: 600 }}>{getOpponent(match)}</span>
                <span style={{ textAlign: 'center' }}>
                  {isHome === true && <span style={homeBadgeStyle}>Heim</span>}
                  {isHome === false && <span style={awayBadgeStyle}>Auswärts</span>}
                  {isHome === null && '-'}
                </span>
                <span>{('matchday' in match && (match.matchday?.competition?.name || match.matchday?.name)) || 'Lokal'}</span>
                <span>
                  {'homeClubId' in match ? (
                    getMatchVehicleAssignments(match.id).length > 0
                      ? getMatchVehicleAssignments(match.id).map((va, vaIdx) => {
                          const driverName = va.traveler?.fullName || va.partnerDriver?.fullName || '';
                          return (
                            <span key={va.id || vaIdx} style={vehicleChipStyle}>
                              {VEHICLE_TYPE_ICONS[va.vehicleType]}
                              {driverName && <span style={{ marginLeft: '3px', fontSize: '11px' }}>{driverName}</span>}
                            </span>
                          );
                        })
                      : <span style={dashStyle}>-</span>
                  ) : null}
                </span>
                <span>
                  {'homeClubId' in match ? (
                    <select style={staffSelectStyle} value={getStaffByRole(match.id, StaffRole.Physio)?.travelerId || ''} onChange={(e) => handleStaffChange(match as Match, StaffRole.Physio, e.target.value ? parseInt(e.target.value) : null)} disabled={savingCell === `${match.id}-${StaffRole.Physio}`} title="Physio">
                      <option value="">-</option>
                      {staffMembers.map(s => <option key={s.id} value={s.id}>{s.fullName}</option>)}
                    </select>
                  ) : null}
                </span>
                <span>
                  {'homeClubId' in match ? (
                    <select style={staffSelectStyle} value={getStaffByRole(match.id, StaffRole.Arzt)?.travelerId || ''} onChange={(e) => handleStaffChange(match as Match, StaffRole.Arzt, e.target.value ? parseInt(e.target.value) : null)} disabled={savingCell === `${match.id}-${StaffRole.Arzt}`} title="Arzt">
                      <option value="">-</option>
                      {staffMembers.map(s => <option key={s.id} value={s.id}>{s.fullName}</option>)}
                    </select>
                  ) : null}
                </span>
                <span>
                  {'homeClubId' in match ? (
                    <select style={staffSelectStyle} value={getStaffByRole(match.id, StaffRole.Athletik)?.travelerId || ''} onChange={(e) => handleStaffChange(match as Match, StaffRole.Athletik, e.target.value ? parseInt(e.target.value) : null)} disabled={savingCell === `${match.id}-${StaffRole.Athletik}`} title="Athletik">
                      <option value="">-</option>
                      {staffMembers.map(s => <option key={s.id} value={s.id}>{s.fullName}</option>)}
                    </select>
                  ) : null}
                </span>
                <span className="segment-actions">
                  {'homeClubId' in match && (
                    <>
                      {onPlanMatch && (
                        <button type="button" className="action-btn action-btn--move" onClick={() => onPlanMatch(match as Match)} title="Planen">
                          {iconCalendar}
                        </button>
                      )}
                      {hasPlanning(match.id) && (
                        <>
                          <button type="button" className="action-btn action-btn--edit" onClick={() => openRouteModal(match.id)} title="Route">
                            {iconRoute}
                          </button>
                          <button type="button" className="action-btn" style={{ color: '#6f42c1' }} onClick={() => {
                            const info = planningInfo.get(match.id);
                            if (info?.planningId) navigate(`/tracking?planningId=${info.planningId}`);
                          }} title="Tracking">
                            {iconTracking}
                          </button>
                        </>
                      )}
                      <button type="button" className="action-btn" style={{ color: '#fd7e14' }} onClick={() => exportToCalendar(matchToCalendarEvent(match as Match))} title="Zum Kalender hinzufügen">
                        📅
                      </button>
                      {onDeleteMatch && ((match as Match).matchType === MatchType.Friendly || (match as Match).matchType === MatchType.Training) && (
                        <button type="button" className="action-btn" style={{ color: '#dc3545' }} onClick={() => onDeleteMatch(match as Match)} title="Spiel löschen">
                          {iconTrash}
                        </button>
                      )}
                    </>
                  )}
                  {'homeTeam' in match && (
                    <button type="button" className="action-btn" style={{ color: '#fd7e14' }} onClick={() => exportToCalendar(localMatchToCalendarEvent(match as LocalMatch))} title="Zum Kalender hinzufügen">
                      📅
                    </button>
                  )}
                </span>
              </div>
            );
          })}
        </React.Fragment>
      ))}
      </div>

      {/* Footer – like .timeline-footer */}
      <div className="timeline-footer">
        <div className="timeline-summary">
          <span className="summary-text">
            {filteredWeeks.reduce((sum, w) => sum + w.matches.length, 0)} Spiele
            {pastMatchCount > 0 && ` | ${pastMatchCount} vergangen`}
          </span>
        </div>
      </div>

      {/* Route Modal */}
      {routeModalOpen && selectedPlanningId && (
        <div style={modalOverlayStyle} onClick={closeRouteModal}>
          <div style={modalContentStyle} onClick={(e) => e.stopPropagation()}>
            <div style={modalHeaderStyle}>
              <h3 style={modalTitleStyle}>Routenübersicht</h3>
              <button type="button" style={modalCloseButtonStyle} onClick={closeRouteModal}>
                ✕
              </button>
            </div>
            <div style={modalBodyStyle}>
              <RouteMap
                planningId={selectedPlanningId}
                onRouteCalculated={(result) => setRouteResult(result)}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Styles – uses CSS classes from MatchdayPlanning.css, only override grid-template-columns
const containerStyle: React.CSSProperties = { overflowX: 'auto' };
// 11-column grid override (CSS .timeline-grid-header / .segment-item have 8 columns for Spieltagsplanung)
const plantafelGridCols = '50px 90px 50px 1.5fr 70px 1fr 1fr 1fr 1fr 1fr 80px';
const gridColsOverride: React.CSSProperties = { gridTemplateColumns: plantafelGridCols };
// Segment row: only override grid-template-columns, rest comes from .segment-item CSS
const segmentRowStyle: React.CSSProperties = { gridTemplateColumns: plantafelGridCols };
const kwColStyle: React.CSSProperties = { fontWeight: 700, textAlign: 'center' };
const englishWeekBadgeStyle: React.CSSProperties = { display: 'block', fontSize: '9px', color: '#856404', backgroundColor: '#ffeeba', padding: '1px 3px', borderRadius: '3px', marginTop: '2px', fontWeight: 600, textAlign: 'center' };
const homeBadgeStyle: React.CSSProperties = { backgroundColor: '#d4edda', color: '#155724', padding: '2px 6px', borderRadius: '10px', fontSize: '11px', whiteSpace: 'nowrap', fontWeight: 600 };
const awayBadgeStyle: React.CSSProperties = { backgroundColor: '#f8d7da', color: '#721c24', padding: '2px 6px', borderRadius: '10px', fontSize: '11px', whiteSpace: 'nowrap', fontWeight: 600 };
const vehicleChipStyle: React.CSSProperties = { backgroundColor: 'rgba(0,0,0,0.08)', padding: '1px 5px', borderRadius: '3px', fontSize: '12px', marginRight: '3px' };
const dashStyle: React.CSSProperties = { color: '#bbb' };

// Modal styles
const modalOverlayStyle: React.CSSProperties = { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 };
const modalContentStyle: React.CSSProperties = { backgroundColor: 'white', borderRadius: '12px', width: '90%', maxWidth: '900px', maxHeight: '90vh', overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,0.2)' };
const modalHeaderStyle: React.CSSProperties = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid #dee2e6', backgroundColor: '#f8f9fa' };
const modalTitleStyle: React.CSSProperties = { margin: 0, fontSize: '18px', fontWeight: 600, color: '#333' };
const modalCloseButtonStyle: React.CSSProperties = { background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#666', padding: '4px 8px' };
const modalBodyStyle: React.CSSProperties = { padding: '20px', maxHeight: 'calc(90vh - 80px)', overflowY: 'auto' };

// Staff select style
const staffSelectStyle: React.CSSProperties = {
  padding: '3px 6px',
  fontSize: '12px',
  fontWeight: 500,
  border: 'none',
  borderRadius: '4px',
  backgroundColor: 'rgba(0,0,0,0.08)',
  cursor: 'pointer',
  minWidth: '90px',
  maxWidth: '130px',
  appearance: 'none' as const,
  WebkitAppearance: 'none' as const,
  backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'8\' height=\'8\' viewBox=\'0 0 24 24\'%3E%3Cpath fill=\'%23666\' d=\'M7 10l5 5 5-5z\'/%3E%3C/svg%3E")',
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'right 4px center',
  paddingRight: '16px',
  color: '#333',
  outline: 'none'
};

export default Plantafel;

